import type { SVGProps } from 'react'

type IconProps = SVGProps<SVGSVGElement>

const base = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  viewBox: '0 0 24 24',
}

// --- Service / feature icons (geometric line style) ---

export function BuildingIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <path d="M4 21h16" />
      <path d="M6 21V5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v16" />
      <path d="M14 21V9h3a1 1 0 0 1 1 1v11" />
      <path d="M9 8h2M9 12h2M9 16h2" />
    </svg>
  )
}

export function HomeIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <path d="M3 11l9-7 9 7" />
      <path d="M5 10v10h14V10" />
      <path d="M10 20v-6h4v6" />
    </svg>
  )
}

export function ConcreteIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <rect x="3" y="4" width="18" height="6" rx="1" />
      <rect x="3" y="14" width="18" height="6" rx="1" />
      <path d="M8 4v6M14 4v6M6 14v6M12 14v6M18 14v6" />
    </svg>
  )
}

export function InteriorIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <path d="M3 20h18" />
      <path d="M4 20v-9l8-5 8 5v9" />
      <rect x="9" y="13" width="6" height="7" />
      <path d="M12 6v2" />
    </svg>
  )
}

export function ExteriorIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <path d="M4 21V7l8-4 8 4v14" />
      <path d="M8 21v-5h8v5" />
      <path d="M8 8h2M14 8h2M8 12h2M14 12h2" />
    </svg>
  )
}

export function RenovationIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3-3a6 6 0 0 1-7.9 7.9l-6.3 6.3a2.1 2.1 0 0 1-3-3l6.3-6.3a6 6 0 0 1 7.9-7.9l-3 3z" />
    </svg>
  )
}

export function PaintIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <rect x="4" y="3" width="13" height="6" rx="1" />
      <path d="M17 6h2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-7" />
      <path d="M9 12v3a2 2 0 0 0 2 2v0" />
      <rect x="9" y="17" width="4" height="4" rx="1" />
    </svg>
  )
}

export function PlumbingIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <path d="M12 2v6" />
      <path d="M8 8h8l-1 4H9L8 8z" />
      <path d="M10 12v4a2 2 0 0 0 2 2h4v-2" />
      <path d="M16 8V6a2 2 0 0 1 2-2h2" />
    </svg>
  )
}

export function ProjectIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18" />
      <path d="M8 4v5M8 13h8M8 16h5" />
    </svg>
  )
}

// --- Feature icons ---

export function QualityIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <path d="M12 2l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 7.7l5.4-.8L12 2z" />
    </svg>
  )
}

export function CommitIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <path d="M9 12l2 2 4-4" />
      <path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z" />
    </svg>
  )
}

export function PrecisionIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </svg>
  )
}

export function TransparencyIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

// --- Contact / UI icons ---

export function PhoneIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />
    </svg>
  )
}

export function WhatsappIcon(p: IconProps): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}>
      <path d="M17.5 14.4c-.3-.15-1.7-.85-2-.94-.26-.1-.45-.15-.65.14-.19.28-.74.94-.9 1.13-.17.19-.34.21-.63.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.04-.17-.29-.02-.45.13-.6.13-.13.3-.34.44-.5.15-.17.2-.29.3-.48.1-.19.05-.36-.02-.5-.08-.15-.65-1.57-.9-2.15-.24-.56-.48-.49-.65-.5h-.56c-.19 0-.5.07-.77.36-.26.29-1 .98-1 2.4 0 1.41 1.03 2.78 1.17 2.97.14.19 2.02 3.08 4.9 4.32.68.29 1.21.47 1.63.6.68.22 1.31.19 1.8.12.55-.08 1.7-.7 1.94-1.36.24-.67.24-1.24.17-1.36-.07-.12-.26-.19-.55-.34zM12 2a10 10 0 0 0-8.5 15.3L2 22l4.8-1.5A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.15l-.3-.18-2.86.9.9-2.78-.2-.3A8.2 8.2 0 1 1 12 20.2z" />
    </svg>
  )
}

export function MailIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  )
}

export function MapPinIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <path d="M12 21s-7-6-7-11a7 7 0 0 1 14 0c0 5-7 11-7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  )
}

export function ArrowIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <path d="M19 12H5" />
      <path d="M12 5l-7 7 7 7" />
    </svg>
  )
}

export function CheckIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  )
}

export function MenuIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  )
}

export function CloseIcon(p: IconProps): React.ReactElement {
  return (
    <svg {...base} {...p}>
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  )
}

// Icon resolver by key (used by data-driven sections)
const iconMap: Record<string, (p: IconProps) => React.ReactElement> = {
  building: BuildingIcon,
  home: HomeIcon,
  concrete: ConcreteIcon,
  interior: InteriorIcon,
  exterior: ExteriorIcon,
  renovation: RenovationIcon,
  paint: PaintIcon,
  plumbing: PlumbingIcon,
  project: ProjectIcon,
  quality: QualityIcon,
  commit: CommitIcon,
  precision: PrecisionIcon,
  transparency: TransparencyIcon,
}

export function Icon({ name, ...props }: IconProps & { name: string }): React.ReactElement {
  const Cmp = iconMap[name] ?? BuildingIcon
  return <Cmp {...props} />
}
